import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { normalizePagination } from '../../utils/pagination.js';
import * as companyUsersAPI from '../../utils/api/companyUsers';

// Async thunks
export const fetchCompanyUsers = createAsyncThunk(
  'companyUsers/fetchCompanyUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await companyUsersAPI.getCompanyUsers(params);
      // Response is already { data: [...], pagination: {...} } from API client
      return response || { data: [], pagination: {} };
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch company users'
      );
    }
  }
);

export const fetchUserLimitInfo = createAsyncThunk(
  'companyUsers/fetchUserLimitInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyUsersAPI.getUserLimitInfo();
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to fetch user limit info'
      );
    }
  }
);

export const createCompanyUser = createAsyncThunk(
  'companyUsers/createCompanyUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await companyUsersAPI.createCompanyUser(userData);
      return response;
    } catch (error) {
      return rejectWithValue({
        message:
          error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to create user',
        errors: error?.errors || error?.response?.data?.errors || null,
      });
    }
  }
);

export const updateCompanyUser = createAsyncThunk(
  'companyUsers/updateCompanyUser',
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const response = await companyUsersAPI.updateCompanyUser(id, userData);
      return response;
    } catch (error) {
      return rejectWithValue({
        message:
          error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to update user',
        errors: error?.errors || error?.response?.data?.errors || null,
      });
    }
  }
);

export const setCompanyUserLoginAccess = createAsyncThunk(
  'companyUsers/setCompanyUserLoginAccess',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await companyUsersAPI.setCompanyUserLoginAccess(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to update login access'
      );
    }
  }
);

export const deleteCompanyUser = createAsyncThunk(
  'companyUsers/deleteCompanyUser',
  async (id, { rejectWithValue }) => {
    try {
      await companyUsersAPI.deleteCompanyUser(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.message ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to delete user'
      );
    }
  }
);

const initialState = {
  users: [],
  primaryAccount: null,
  userLimitInfo: {
    canAdd: true,
    currentCount: 0,
    maxUsers: 1
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  loading: false,
  error: null,
  filters: {
    status: '',
    role: '',
    search: ''
  }
};

const companyUsersSlice = createSlice({
  name: 'companyUsers',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch company users
      .addCase(fetchCompanyUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.users = action.payload.data || [];
          state.pagination = normalizePagination(
            action.payload.pagination || {},
            state.pagination
          );
          state.primaryAccount = action.payload.primaryAccount ?? null;
        } else {
          state.users = [];
          state.primaryAccount = null;
        }
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch user limit info
      .addCase(fetchUserLimitInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserLimitInfo.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.userLimitInfo = action.payload || state.userLimitInfo;
        }
      })
      .addCase(fetchUserLimitInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create company user
      .addCase(createCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.unshift(action.payload);
        // currentCount includes the main company admin account (min 1)
        state.userLimitInfo.currentCount = Math.max(1, (state.userLimitInfo.currentCount || 1) + 1);
        state.userLimitInfo.canAdd = state.userLimitInfo.currentCount < state.userLimitInfo.maxUsers;
      })
      .addCase(createCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update company user
      .addCase(updateCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Set company user login access
      .addCase(setCompanyUserLoginAccess.pending, (state) => {
        state.error = null;
      })
      .addCase(setCompanyUserLoginAccess.fulfilled, (state, action) => {
        const index = state.users.findIndex(user => user.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(setCompanyUserLoginAccess.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete company user
      .addCase(deleteCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(user => user.id !== action.payload);
        // Never go below 1 because the company admin account always exists
        state.userLimitInfo.currentCount = Math.max(1, (state.userLimitInfo.currentCount || 1) - 1);
        state.userLimitInfo.canAdd = state.userLimitInfo.currentCount < state.userLimitInfo.maxUsers;
      })
      .addCase(deleteCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setFilters, setPagination, clearError } = companyUsersSlice.actions;
export default companyUsersSlice.reducer;
